/**
 * Actycrea: swiper.esm.js (ES6)
 * MIT
 */

import Swiper from 'swiper';
import { Pagination, Autoplay, Navigation, Mousewheel , Grid , EffectFade } from 'swiper/modules';
import { PREFIX } from '../helpers/config.js';
import { deepMerge } from '../helpers/helpers.js';
import { getCurrentBreakpoint } from '../helpers/breakpoints.js';
import { onResizeEnd , onActive } from '../helpers/events.js';
import { animateSlideText } from '../helpers/splitText.js';
import AOS from 'aos';

// Selectores con PREFIX + fallback legado
const SELECTOR = `data-${PREFIX}-mysw`
const SELECTOR_DATA = `[${SELECTOR}]`;
const SELECTOR_PAGINATION = `[data-${PREFIX}-pagination]`;
const SELECTOR_SWIPER = `[data-${PREFIX}-swiper]`;
const SELECTOR_NEXT = `[data-${PREFIX}-swiper-next]`;
const SELECTOR_PREV = `[data-${PREFIX}-swiper-prev]`;
const SELECTOR_SLIDE = `[data-${PREFIX}-slide]`;
const SELECTOR_TITLE = `data-${PREFIX}-title`;
const DATA_KEY_SLIDE   = `${PREFIX}Slide`;

const SELECTOR_AVATAR = `data-${PREFIX}-avatar`;
const SELECTOR_YEAR = `data-${PREFIX}-year`;

const CLASS_ACTIVE_NAV = 'active';

const DEFAULTS = {
  pagination: false,
  navigation: false,
  type: '',
  merge: {},
  modules: {
    pagination: {
      el: SELECTOR_PAGINATION,
      clickable: true,
      type: 'fraction',
    },
    navigation: {
      hideOnClick: true,
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    }
  },
  params: {
    modules: [ Navigation, Pagination, Autoplay, Mousewheel, Grid, EffectFade ],
    speed: 800,
    slidesPerView: 1,
    spaceBetween: 0,
    autoplay: { delay: 5000 }
  },
  videoSlideEndNext: {
    enabled: false,
    restartOnChange: true,
    stopSwiperAutoplay: true,
    loopToStart: true
  },
};

export class MySW {
  constructor( element, config = {} ) {
    if ( !( element instanceof HTMLElement ) ) throw new Error( 'MySW: element inválido' );

    this._element = element;
    this._config = deepMerge( DEFAULTS, config );

    this._prevEl = element.querySelector( SELECTOR_PREV );
    this._nextEl = element.querySelector( SELECTOR_NEXT );

    this.swiperEl = element.querySelector( SELECTOR_SWIPER );

    this.avatarEl = null;
    this.yearsEl = null;

    let params_ = { ...this._config.params };

    // Paginación
    if ( this._config.pagination ) {
      params_.pagination = { ...this._config.modules.pagination };
      params_.pagination.el = element.querySelector( params_.pagination.el );
      if ( params_.pagination.type == 'fraction' ) {
        params_.pagination.renderFraction = ( currentClass, totalClass ) => {
          return '<span class="' + currentClass + '"></span>' +
            ' · ' +
            '<span class="' + totalClass + '"></span>';
        }
      }
      this._config.pagination = false;
    }

    // Navegación
    if ( this._config.navigation ) {
      params_.navigation = { ...this._config.modules.navigation };
      params_.navigation.nextEl = element.querySelector( params_.navigation.nextEl ) || element.querySelector( SELECTOR_NEXT );
      params_.navigation.prevEl = element.querySelector( params_.navigation.prevEl ) || element.querySelector( SELECTOR_PREV );
      this._config.navigation = false;
    }

    let merge_ = this._config.merge;
    if ( typeof merge_ === 'string' ) {
      try { merge_ = JSON.parse( merge_ ); } catch ( e ) { merge_ = {}; }
    }

    const config_ = deepMerge( params_, merge_ );

    console.log( 'MySW: => constructor => config_ ' , config_ );

    config_.on = { init: ( swiper ) => this._init( swiper ) };
    this._swiper = new Swiper( this.swiperEl, config_ );

    this._bind( );
  }

  _bind( ) {
    this._prevEl?.addEventListener( 'click', ( event ) => this._prevSlide( event ), false );
    this._nextEl?.addEventListener( 'click', ( event ) => this._nextSlide( event ), false );
    window.addEventListener( onResizeEnd, ( event ) => this._refresh( event ), false );
    if( this._config.type == 'contact' ){
      this._map = this._map.bind( this );
      this._element.addEventListener( 'click' , this._map , false );
    }
  }

  _init( swiper ) {
    this._createAvatarNavigation( swiper );
    this._createYearNavigation( swiper );
    swiper.on( 'slideChange', ( swiper ) => this._slideChange( swiper ) );
    if ( this._config.videoSlideEndNext?.enabled ) {
      this._initVideoSlideEndNext( swiper );
    }
    if( this._config.type == 'contact' ){
      setTimeout( ( ) => {
        this._map({ target: this._element.querySelector( SELECTOR_SLIDE ) });
      } , 500 );
    }
    if( this._config.type == 'progressbar' ){
      this._createCustomLabels( swiper );
    
      // Animación inicial al cargar
      this._triggerSlideAnimation( swiper );

      // 1. Resetear al EMPEZAR el cambio
      swiper.on( 'slideChange', ( s ) => {
        this._clearAllActiveWords( s );
      });

      // 2. Animar al TERMINAR el cambio
      swiper.on( 'slideChangeTransitionEnd', ( s ) => {
        this._triggerSlideAnimation( s );
      });
    }
    setTimeout( ( ) => AOS.refresh( ), 500 );
  }

  _slideChange( swiper ) {
    if ( this.avatarEl !== null && this.avatarEl.childNodes[ swiper.activeIndex ] ) {
      this.avatarEl.childNodes.forEach( el => el.classList.remove( CLASS_ACTIVE_NAV ) );
      this.avatarEl.childNodes[ swiper.activeIndex ].classList.add( CLASS_ACTIVE_NAV );
    }
    if ( this.yearsEl !== null && this.yearsEl.childNodes[ swiper.activeIndex ] ) {
      this.yearsEl.childNodes.forEach( el => el.classList.remove( CLASS_ACTIVE_NAV ) );
      this.yearsEl.childNodes[ swiper.activeIndex ].classList.add( CLASS_ACTIVE_NAV );
      this._updateYearsVars( this.yearsEl.childNodes[ swiper.activeIndex ] );
    }
    if( this._config.type == 'contact' ){
      this._map({ target: this._element.querySelector( `[data-${PREFIX}-slide="${swiper.activeIndex}"]` ) });
      console.log( 'MySW: => _slideChange => activeIndex ' , swiper.activeIndex );
    }
    this._updateActiveLabel( swiper.activeIndex );
  }

  _refresh( event ) {
    if ( this.yearsEl !== null && this.yearsEl.childNodes[ this._swiper.activeIndex ] ) {
      this._updateYearsVars( this.yearsEl.childNodes[ this._swiper.activeIndex ] );
    }
  }

  _nextSlide( event ) {
    this._swiper.slideNext( );
  }

  _prevSlide( event ) {
    this._swiper.slidePrev( );
  }

  _goToSlide( event ) {
    event.preventDefault( );
    const btn = event.currentTarget || event.target.closest( '.swiper-navigation-avatar-item' );
    const index = Number( btn?.dataset?.index );
    if ( Number.isFinite( index ) ) this._swiper.slideTo( index );
  }

  _createCustomLabels( swiper ) {
    // 1. Buscamos el nodo de la paginación nativa
    const paginationEl = this._element.querySelector( SELECTOR_PAGINATION );
    if ( !paginationEl ) return;

    // 2. Buscamos al padre real (el <nav>)
    const parentEl = paginationEl.closest('.swiper-pagination-parent');
    if ( !parentEl ) {
      console.error("MySW: No se encontró .swiper-pagination-parent");
      return;
    }

    // Si ya existen los labels, no los dupliques (importante para hot-reload o reinicios)
    if (parentEl.querySelector('.swiper-custom-labels')) return;

    const labelsContainer = document.createElement( 'div' );
    labelsContainer.className = 'swiper-custom-labels';

    swiper.slides.forEach( ( slideEl, index ) => {
      // Obtenemos el título del atributo que definimos antes
      const title = slideEl.getAttribute( SELECTOR_TITLE ) || `Slide ${index + 1}`;
      
      const span = document.createElement( 'span' );
      span.className = 'custom-label-item';
      span.textContent = title;
      span.dataset.index = String( index );

      span.addEventListener( 'click', (e) => {
        e.preventDefault();
        this._swiper.slideTo( index );
      });

      labelsContainer.appendChild( span );
    });

    // 3. LA CLAVE: Inyectarlo como primer hijo del padre para que quede arriba
    parentEl.prepend( labelsContainer );
    
    this.labelsEl = labelsContainer;
    this._updateActiveLabel( swiper.activeIndex );
  }

  _initVideoSlideEndNext( swiper ) {
    this._videoElements = Array.from( this.swiperEl.querySelectorAll( 'video' ) );

    if ( !this._videoElements.length ) return;

    if (
      this._config.videoSlideEndNext?.stopSwiperAutoplay &&
      swiper.autoplay &&
      typeof swiper.autoplay.stop === 'function'
    ) {
      swiper.autoplay.stop();
    }

    this._videoElements.forEach( ( video ) => {
      video.removeAttribute( 'loop' );

      video.addEventListener( 'ended', () => {
        this._goToNextVideoSlide( swiper );
      } );
    } );

    swiper.on( 'slideChangeTransitionStart', () => {
      if ( this._config.videoSlideEndNext?.restartOnChange ) {
        this._pauseAndResetAllVideos();
      } else {
        this._pauseAllVideos();
      }
    } );

    swiper.on( 'slideChangeTransitionEnd', () => {
      this._playActiveSlideVideo( swiper );
    } );

    this._pauseAndResetAllVideos();
    this._playActiveSlideVideo( swiper );
  }

  _pauseAllVideos( ) {
    if ( !this._videoElements?.length ) return;

    this._videoElements.forEach( ( video ) => {
      try {
        video.pause();
      } catch ( e ) {}
    } );
  }

  _pauseAndResetAllVideos( ) {
    if ( !this._videoElements?.length ) return;

    this._videoElements.forEach( ( video ) => {
      try {
        video.pause();
        video.currentTime = 0;
      } catch ( e ) {}
    } );
  }

  _getActiveSlideVideo( swiper ) {
    const activeSlide = swiper?.slides?.[ swiper.activeIndex ];
    if ( !activeSlide ) return null;
    return activeSlide.querySelector( 'video' );
  }

  _playActiveSlideVideo( swiper ) {
    const activeVideo = this._getActiveSlideVideo( swiper );
    if ( !activeVideo ) return;

    const tryPlay = () => {
      const playPromise = activeVideo.play();

      if ( playPromise && typeof playPromise.catch === 'function' ) {
        playPromise.catch( ( error ) => {
          console.warn( 'MySW: vídeo no pudo reproducirse', error );
        } );
      }
    };

    if ( activeVideo.readyState >= 2 ) {
      tryPlay();
      return;
    }

    const onCanPlay = () => {
      activeVideo.removeEventListener( 'canplay', onCanPlay );
      tryPlay();
    };

    activeVideo.addEventListener( 'canplay', onCanPlay, { once: true } );
    activeVideo.load();
  }

  _goToNextVideoSlide( swiper ) {
    if ( swiper.isEnd ) {
      if ( this._config.videoSlideEndNext?.loopToStart ) {
        swiper.slideTo( 0 );
      }
      return;
    }

    swiper.slideNext();
  }

  // Nueva función para apagar el texto del slide entrante
  _clearAllActiveWords() {
    const allWords = this.swiperEl.querySelectorAll('.sp-word');
    allWords.forEach(word => word.classList.remove('active'));
  }

  _triggerSlideAnimation( swiper ) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    const textToAnimate = activeSlide.querySelector(`[data-${PREFIX}-split]`);

    
    if ( textToAnimate ) {
      animateSlideText( textToAnimate );
      // const allWords = this.swiperEl.querySelectorAll('.sp-word');
      // allWords.forEach(word => word.classList.remove('active'));
    }
  }

  _updateActiveLabel( index ) {
    if ( !this.labelsEl ) return;
    const labels = this.labelsEl.querySelectorAll( '.custom-label-item' );
    
    labels.forEach( ( label, i ) => {
      const isActive = i === index;
      label.classList.toggle( CLASS_ACTIVE_NAV, isActive );

      // Si es el label activo, lo centramos en el contenedor
      if (isActive) {
        this._scrollLabelIntoView(label);
      }
    });
  }

  _scrollLabelIntoView(label) {
    const container = this.labelsEl;
    
    // Calculamos la posición para centrar el label
    const labelOffset = label.offsetLeft;
    const labelWidth = label.offsetWidth;
    const containerWidth = container.offsetWidth;

    // El cálculo: posición del label - mitad del contenedor + mitad del label
    const scrollPos = labelOffset - (containerWidth / 2) + (labelWidth / 2);

    container.scrollTo({
      left: scrollPos,
      behavior: 'smooth'
    });
  }

  _createAvatarNavigation( swiper ) {
    if ( this.swiperEl.querySelector( '.swiper-navigation-avatar' ) ) return;
    const frag = document.createDocumentFragment( );
    swiper.slides.forEach( ( el, index ) => {
      const url = el.getAttribute( SELECTOR_AVATAR );
      if ( !url ) return;
      const a = document.createElement( 'a' );
      a.classList.add( 'swiper-navigation-avatar-item' );
      if ( index == 0 ) a.classList.add( CLASS_ACTIVE_NAV );
      a.href = '#';
      a.dataset.index = String( index );
      a.style.backgroundImage = `url('${url}')`;
      frag.appendChild( a );
    } );
    if ( frag.childNodes.length == 0 ) return;
    this.avatarEl = document.createElement( 'nav' );
    this.avatarEl.className = 'swiper-navigation-avatar';
    this.avatarEl.appendChild( frag );
    this.avatarEl.addEventListener( 'click', ( ev ) => {
      const btn = ev.target.closest( '.swiper-navigation-avatar-item' );
      if ( !btn ) return;
      ev.preventDefault( );
      const index = Number( btn?.dataset?.index );
      if ( Number.isFinite( index ) ) this._swiper.slideTo( index );
    }, false );
    this.swiperEl.prepend( this.avatarEl );
  }

  _createYearNavigation( swiper ) {
    if ( this.swiperEl.querySelector( '.swiper-navigation-year' ) ) return;
    const frag = document.createDocumentFragment( );
    swiper.slides.forEach( ( el, index ) => {
      const year = el.getAttribute( SELECTOR_YEAR );
      if ( !year ) return;
      const a = document.createElement( 'a' );
      a.classList.add( 'swiper-navigation-year-item' );
      if ( index == 0 ) a.classList.add( CLASS_ACTIVE_NAV );
      a.href = '#';
      a.dataset.index = String( index );
      a.textContent = year;
      frag.appendChild( a );
    } );
    if ( frag.childNodes.length == 0 ) return;
    this.yearsEl = document.createElement( 'nav' );
    this.yearsEl.className = 'swiper-navigation-year';
    this.yearsEl.appendChild( frag );
    this.yearsEl.addEventListener( 'click', ( ev ) => {
      const btn = ev.target.closest( '.swiper-navigation-year-item' );
      if ( !btn ) return;
      ev.preventDefault( );
      const index = Number( btn?.dataset?.index );
      if ( Number.isFinite( index ) ) this._swiper.slideTo( index );
    }, false );
    const wrapper = document.createElement( 'div' );
    wrapper.className = 'swiper-navigation-year-scroll';
    wrapper.appendChild( this.yearsEl );
    this.swiperEl.append( wrapper );
    this._updateYearsVars( this.yearsEl.childNodes[ 0 ] );
  }

  _updateYearsVars( btn ) {
    if ( !btn ) return;
    const rectBtn = btn.getBoundingClientRect( );
    const rectNav = this.yearsEl.getBoundingClientRect( );
    const left = rectBtn.left - rectNav.left;
    const width = rectBtn.width;
    this.yearsEl.style.setProperty( `--${PREFIX}-position-left`, `${left}px` );
    this.yearsEl.style.setProperty( `--${PREFIX}-size-width`, `${width}px` );
  }

  _map( event ) {
    console.log( 'MySW: => _map => event ' , event );
    const btn = event.target.closest( SELECTOR_SLIDE );
    if ( !btn || btn.classList.contains( CLASS_ACTIVE_NAV ) ) return;
    let active = this._element.querySelector( `${SELECTOR_SLIDE}.${CLASS_ACTIVE_NAV}` );
    if( active ){
      active.classList.remove( CLASS_ACTIVE_NAV );
    }
    btn.classList.add( CLASS_ACTIVE_NAV );
    window.dispatchEvent( new CustomEvent( onActive , {
      detail: { index: btn.dataset[ DATA_KEY_SLIDE ] }
    } ) );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ) {
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if ( el._myswInstance ) return;
      let cfg = {};
      const raw = el.getAttribute( SELECTOR );
      if ( raw ) {
        try {
          cfg = JSON.parse( raw );
        } catch ( e ) { cfg = {}; }
      }
      el._myswInstance = new MySW( el, cfg );
      instances.push( el._myswInstance );
    } );
    return instances;
  }
}

export default MySW;
